import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PublicNav from "@/components/nav/PublicNav";
import Footer from "@/components/nav/Footer";
import { Check, ArrowRight, Sparkles, Users } from "lucide-react";
import { TRIAL_CTA_TEXT, TRIAL_CTA_SUBTEXT } from "@/components/trial/TrialCTA";

const INDUSTRY_BLURB = {
  "hoa-steward": "Runs the HOA back office — inbox triage, violations, ARC intake, board summaries.",
  "homihq": "Keeps every listing, lead, and transaction moving — from first touch to close.",
  "foundation-first": "Handles the job-site paperwork — permits, subs, updates, and 811 locate assistance.",
};

// Per-employee plan tiers (display-only; billing not wired yet).
const PLAN_TIERS = {
  "homihq": {
    annual_note: "Annual billing: ~15% off",
    tiers: [
      { name: "Agent",     price: "$39/mo",  note: null },
      { name: "Agent Pro", price: "$79/mo",  note: "Founding Offer: $49/mo for the first 100 members" },
      { name: "Team",      price: "$149/mo", note: null },
    ],
  },
  "hoa-steward": {
    annual_note: "Annual billing: ~15% off",
    tiers: [
      { name: "Pilot",              price: "$299/mo",     note: null },
      { name: "Portfolio",          price: "$599/mo",     note: null },
      { name: "Management Company", price: "$1,250+/mo",  note: "Custom" },
    ],
  },
  "foundation-first": {
    annual_note: "Annual billing: ~15% off · Pay-as-you-go: $49 flat fee",
    tiers: [
      { name: "Founding Contractor", price: "$79/mo",   note: "First 3 months · first 5–10 pilot companies · requires feedback + testimonial · then $149/mo" },
      { name: "Contractor",          price: "$149/mo",  note: null },
      { name: "Construction Team",   price: "$299/mo",  note: null },
      { name: "Enterprise / Custom", price: "$499+/mo", note: null },
    ],
  },
};

export default function Pricing() {
  const [employees, setEmployees] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/employees")
      .then(({ data }) => setEmployees(data.filter(e => e.status !== "Coming Soon")))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <PublicNav />

      {/* HERO */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="brand-eyebrow mb-6">PRICING</div>
          <h1 className="brand-headline text-5xl md:text-8xl leading-[0.95]">
            Choose your AI employee.<br />
            <span className="text-ivory">Subscribe to their plan.</span><br />
            <span className="text-purple italic">No platform fee.</span>
          </h1>
          <p data-testid="pricing-value-line" className="text-gold/80 text-base md:text-lg mt-8 max-w-2xl tracking-wide">
            No platform fees. No software bundle. Pay only for the employee you hire.
          </p>
          <p className="text-steel text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            Hired is the free platform — the dashboard, engine, and approvals system that runs your team.
            You only pay for the AI employees you hire. Start any employee free for 14 days, no credit card.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <div data-testid="pricing-no-fee-badge" className="inline-flex items-center gap-2 border border-gold/50 px-4 py-2 text-xs tracking-widest font-bold text-gold">
              <Check size={14} /> HIRED PLATFORM · $0
            </div>
          </div>
        </div>
      </section>

      {/* EMPLOYEE PRODUCT CARDS */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="brand-eyebrow mb-6">THE EMPLOYEES</div>
          <h2 className="brand-headline text-4xl md:text-6xl max-w-3xl mb-14">
            Pick who you want on payroll.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {employees.map((e, idx) => {
              const highlight = idx === 1; // middle card = ivory
              return (
                <div key={e.slug} data-testid={`pricing-employee-${e.slug}`}
                     className={highlight ? "card-white p-10 flex flex-col" : "border border-line p-10 hover:border-gold transition-colors flex flex-col bg-graphite/30"}>
                  <div className="flex items-center justify-between">
                    <div className="brand-eyebrow" style={highlight ? {color:'#0B0D10'} : {}}>{e.industry?.toUpperCase()}</div>
                    <div className={`text-[10px] tracking-widest font-bold ${highlight ? "text-purple" : "text-purple"}`}>PREVIEW</div>
                  </div>
                  <div className={`brand-headline text-4xl md:text-5xl mt-6 ${highlight ? "text-ink" : "text-ivory"}`}>{e.name}</div>
                  <div className={`text-sm mt-2 ${highlight ? "text-ink/70" : "text-steel"}`}>{e.role_title}</div>
                  <p className={`text-sm mt-5 leading-relaxed ${highlight ? "text-ink" : "text-ivory/85"}`}>
                    {INDUSTRY_BLURB[e.slug] || e.tagline}
                  </p>

                  {/* Plan tiers */}
                  <div className={`mt-6 border-t pt-5`}
                       style={highlight ? {borderColor:'rgba(0,0,0,0.1)'} : {borderColor:'#22262E'}}>
                    <div className="brand-eyebrow" style={highlight ? {color:'#0B0D10'} : {}}>PLANS</div>
                    <div data-testid={`pricing-tiers-${e.slug}`} className="mt-3 space-y-3">
                      {(PLAN_TIERS[e.slug]?.tiers || []).map((t, ti) => (
                        <div key={ti} data-testid={`pricing-tier-${e.slug}-${ti}`} className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className={`text-sm font-semibold ${highlight ? "text-ink" : "text-ivory"}`}>{t.name}</div>
                            {t.note && (
                              <div className={`text-[11px] mt-1 leading-snug ${highlight ? "text-ink/60" : "text-steel"}`}>{t.note}</div>
                            )}
                          </div>
                          <div className={`text-sm font-mono shrink-0 ${highlight ? "text-purple" : "text-gold"}`}>{t.price}</div>
                        </div>
                      ))}
                    </div>
                    {PLAN_TIERS[e.slug]?.annual_note && (
                      <div className={`mt-4 text-[11px] tracking-wide ${highlight ? "text-ink/60" : "text-steel"}`}>
                        {PLAN_TIERS[e.slug].annual_note}
                      </div>
                    )}
                  </div>

                  <div className="mt-auto pt-8">
                    <button
                      data-testid={`pricing-cta-${e.slug}`}
                      onClick={() => nav(`/signup?employee=${e.slug}`)}
                      className={`w-full text-center py-4 font-bold tracking-wide transition-colors ${highlight ? "bg-ink text-white hover:bg-gold hover:text-ink" : "btn-gold justify-center"}`}
                    >
                      {TRIAL_CTA_TEXT} <ArrowRight size={16} className="inline ml-2" />
                    </button>
                    <div data-testid={`pricing-cta-${e.slug}-sub`} className={`mt-3 text-xs tracking-wide ${highlight ? "text-ink/70" : "text-steel"}`}>{TRIAL_CTA_SUBTEXT}</div>
                    <button
                      onClick={() => nav(`/hired/${e.slug}`)}
                      className={`mt-4 text-xs tracking-widest font-semibold inline-flex items-center gap-1 ${highlight ? "text-obsidian/70 hover:text-purple" : "text-steel hover:text-ivory"}`}
                    >
                      SEE WHAT {e.name.toUpperCase()} DOES →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMING SOON — CUSTOM + BUNDLES */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="brand-eyebrow mb-6">WHAT&apos;S NEXT</div>
          <h2 className="brand-headline text-4xl md:text-6xl max-w-3xl mb-14">
            More ways to <span className="text-purple italic">build a team.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ComingSoonCard
              icon={<Sparkles size={22} strokeWidth={1.5} />}
              title="Custom Employee Creation"
              body="Design a bespoke AI employee for a workflow that isn't covered yet. Bring your process, we'll build the employee."
              testid="section-custom-employee"
            />
            <ComingSoonCard
              icon={<Users size={22} strokeWidth={1.5} />}
              title="Multi-Employee Bundles"
              body="Combine two or more AI employees at a bundled rate. Great for teams running HOA, real estate, and construction in parallel."
              testid="section-bundles"
            />
          </div>
        </div>
      </section>

      {/* ENTERPRISE / TALK-TO-HIRED */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="border border-line p-10 md:p-14 bg-graphite/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
              <div className="md:col-span-2">
                <div className="brand-eyebrow mb-4">CUSTOM DEPLOYMENTS</div>
                <div className="brand-headline text-3xl md:text-5xl">
                  Running the whole back office?<br />
                  <span className="text-purple italic">Let&apos;s talk.</span>
                </div>
                <p className="text-steel mt-6 max-w-2xl">
                  For teams with unique workflows, industry-specific requirements, or SLA needs — we&apos;ll design a
                  Hired deployment around your operation. Still no platform fee, priced per employee.
                </p>
              </div>
              <div className="md:col-span-1 md:text-right">
                <button
                  data-testid="pricing-talk-to-hired"
                  onClick={() => nav("/contact?source=pricing_custom")}
                  className="btn-outline-gold"
                >
                  Talk to Hired <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ComingSoonCard({ icon, title, body, testid }) {
  return (
    <div data-testid={testid} className="border border-line p-10 bg-graphite/30 relative opacity-95">
      <div className="absolute top-6 right-6">
        <span data-testid={`${testid}-badge`} className="text-[10px] tracking-widest font-bold text-purple border border-purple/50 px-2 py-1">
          COMING SOON
        </span>
      </div>
      <div className="text-gold">{icon}</div>
      <div className="brand-headline text-3xl md:text-4xl mt-6">{title}</div>
      <p className="text-steel mt-4 leading-relaxed">{body}</p>
      <button
        disabled
        aria-disabled="true"
        data-testid={`${testid}-cta`}
        className="mt-8 inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold tracking-wide border border-line text-steel cursor-not-allowed"
      >
        Notify me when available
      </button>
    </div>
  );
}
